<template>
  <div>
    <v-simple-table>
      <thead>
      <tr>
        <th>{{ trans('booking.consumption.type') }}</th>
        <th style="width: 150px;">{{ trans('booking.consumption.noConsumptionAccrued') }}</th>
        <th>{{ trans('booking.consumption.old') }}</th>
        <th>{{ trans('booking.consumption.new') }}</th>
        <th>{{ trans('booking.consumption.consumption') }}</th>
        <th>{{ trans('booking.consumption.amount') }}</th>
      </tr>
      </thead>
      <tbody>
        <tr v-if="!consumptions || Object.values(consumptions).length === 0">
          <td colspan="99">
            <div class="no-data-placeholder d-flex text-caption justify-center">{{ trans('booking.consumption.noConsumptionAvailable') }}</div>
          </td>
        </tr>
        <tr
          v-for="(bookingConsumption, i) in Object.values(consumptions)"
          :key="'booking-consumption-type-' + bookingConsumption.type"
        >
          <td>
            {{ trans('consumption.types.' + bookingConsumption.type) }}
          </td>
          <td>
            <v-checkbox
              v-if="canEditConsumptions"
              v-model="bookingConsumption.noConsumptionAccrued"
              :error-messages="validationFieldErrors.consumptions && validationFieldErrors.consumptions[i] ? validationFieldErrors.consumptions[i].noConsumptionAccrued : []"
            />
            <template v-else>
              <boolean-flag-icon v-if="bookingConsumption.noConsumptionAccrued" :value="bookingConsumption.noConsumptionAccrued" />
              <span v-else>&ndash;</span>
            </template>
          </td>
          <td>
            <form-input
              cols="12"
              v-model="bookingConsumption.amountOld"
              type="currency"
              :error-messages="validationFieldErrors.consumptions && validationFieldErrors.consumptions[i] ? validationFieldErrors.consumptions[i].amountOld : []"
              :currency="trans('consumption.typeUnits.' + bookingConsumption.type)"
              :precision="4"
              :readonly="!canEditConsumptions"
              @input="onAmountUpdated($event, bookingConsumption)"
            />
          </td>
          <td>
            <form-input
              cols="12"
              v-model="bookingConsumption.amountNew"
              type="currency"
              :error-messages="validationFieldErrors.consumptions && validationFieldErrors.consumptions[i] ? validationFieldErrors.consumptions[i].amountNew : []"
              :currency="trans('consumption.typeUnits.' + bookingConsumption.type)"
              :precision="4"
              :readonly="!canEditConsumptions"
              @input="onAmountUpdated($event, bookingConsumption)"
            />
          </td>
          <td>
            <currency-amount
              :amount="bookingConsumption.amountNew - bookingConsumption.amountOld"
              :currency="trans('consumption.typeUnits.' + bookingConsumption.type)"
              :precision="4"
            />
          </td>
          <td>
            <currency-amount v-if="hasValidEntries(bookingConsumption)" :amount="calculateCost(bookingConsumption)" currency="EUR" />
            <span v-else>&ndash;</span>
          </td>
        </tr>
      </tbody>
      <tfoot>
      <tr>
        <th colspan="2">
          <global-validation-errors class="mt-2" :value="validationErrors" />
        </th>
        <th colspan="3">
          <div v-if="canEditConsumptions" class="d-flex justify-end">
            <save-button small @click="updateConsumption" :loading="updating" />
          </div>
        </th>
        <th><currency-amount :amount="totalCost" currency="EUR" :amount-font-size="15"/></th>
      </tr>
      </tfoot>
    </v-simple-table>
  </div>
</template>

<script>
import {LocaleUtils} from '@/utils/locale-utils.js';
import {BookingApi} from '@/api/booking-api.js';
import {ObjectApi} from '@/api/object-api.js';
import CurrencyAmount from '@/components/CurrencyAmount.vue';
import FormInput from '@/components/FormInput.vue';
import GlobalValidationErrors from '@/components/GlobalValidationErrors.vue';
import SaveButton from '@/components/SaveButton.vue';
import {MainUtils} from '@/utils/main-utils.js';
import BooleanFlagIcon from '@/components/BooleanFlagIcon.vue';

export default {
  name: 'BookingConsumptions',
  components: {BooleanFlagIcon, SaveButton, GlobalValidationErrors, FormInput, CurrencyAmount},
  mixins: [
    MainUtils,
    LocaleUtils
  ],
  props: {
    /** @type {BookingDto} */
    booking: {
      required: true
    },
    canEditConsumptions: {
      type: Boolean,
      default () {
        return false;
      }
    }
  },
  data () {
    return {
      /** @type {ObjectConsumptionDto[]} */
      objectConsumptions: [],
      /** @type {BookingConsumptionDto[]} */
      consumptions: {},
      validationErrors: {},
      updating: false
    };
  },
  computed: {
    totalCost () {
      let totalCost = 0;

      Object.values(this.consumptions).forEach(consumption => totalCost += this.calculateCost(consumption));

      return totalCost;
    }
  },
  methods: {
    getObjectConsumptions () {
      return new Promise((resolve, reject) => {
        ObjectApi.getById(this.booking.objectId)
          .then(response => {
            this.objectConsumptions = response.data.consumptions;
            resolve();
          })
      });
    },
    initConsumptions () {
      /** @var {BookingDto} booking  */
      let booking = this.booking;
      this.$set(this, 'consumptions', {});

      this.getObjectConsumptions()
        .then(() => {
          this.objectConsumptions?.forEach(objectConsumption => {
            if (objectConsumption.amountPrice > 0) {
              this.$set(this.consumptions, objectConsumption.type, {
                type: objectConsumption.type,
                noConsumptionAccrued: false,
                amountOld: null,
                amountNew: null
              });
            }
          });

          booking.consumptions?.forEach( (bookingConsumption) => {
            this.$set(this.consumptions, bookingConsumption.type, bookingConsumption);
          });
        })
    },
    /** @param {BookingConsumptionDto} consumption */
    calculateCost (consumption) {
      let objectConsumption = this.objectConsumptions.filter(objectConsumption => objectConsumption.type === consumption.type)[0];
      if (objectConsumption) {
        return (consumption.amountNew - consumption.amountOld) * objectConsumption.amountPrice;
      }
      return 0;
    },
    /** @param {BookingConsumptionDto} consumption */
    hasValidEntries (consumption) {
      return consumption.amountNew > 0 && consumption.amountOld > 0;
    },
    /**
     * @param {number|null} amount
     * @param {BookingConsumptionDto} consumption
     */
    onAmountUpdated (amount, consumption) {
      if (amount > 0 && Math.abs(consumption.amountOld - consumption.amountNew) > 0.0001) {
        consumption.noConsumptionAccrued = false;
      }

    },
    updateConsumption() {
      this.updating = true;
      BookingApi.updateBookingConsumptions(this.booking.id, {consumptions: Object.values(this.consumptions)})
        .then(response => {
          this.validationErrors = {};
          this.showSavedSuccessfullyNotification();
        })
        .then(() => {
          this.$emit('save');
        })
        .catch((response) => this.validationErrors = this.getValidationErrors(response))
        .finally(() => this.updating = false);
    },
  },
  watch: {
    booking: {
      immediate: true,
      handler () {
        this.initConsumptions();
      }
    }
  }
}
</script>

<style scoped>

</style>
